import React from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
} from 'react-native';
import { router } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';

export default function TermosDeUsoScreen() {
  const { colors } = useTheme();

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    backButton: {
      marginRight: 16,
    },
    headerTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    content: {
      flex: 1,
      paddingHorizontal: 16,
      paddingVertical: 20,
    },
    title: {
      fontSize: 24,
      fontFamily: 'Inter-Bold',
      color: colors.text,
      marginBottom: 8,
    },
    lastUpdate: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.textSecondary,
      marginBottom: 20,
    },
    paragraph: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      lineHeight: 24,
      marginBottom: 16,
    },
    sectionTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginTop: 20,
      marginBottom: 12,
    },
    bulletPoint: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      lineHeight: 24,
      marginBottom: 8,
      marginLeft: 16,
    },
    email: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    highlight: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
  });

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => router.back()}
          activeOpacity={0.7}
        >
          <ArrowLeft size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Termos de Uso</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <Text style={styles.title}>Termos e Condições de Uso - Põe no Racha</Text>
        <Text style={styles.lastUpdate}>
          Última atualização: 06 de janeiro de 2026
        </Text>

        <Text style={styles.paragraph}>
          Bem-vindo(a) ao Põe no Racha! Estes termos e condições descrevem as regras para usar nosso aplicativo e serviços. Ao criar uma conta e utilizar o Põe no Racha, você concorda com estes termos.
        </Text>

        <Text style={styles.sectionTitle}>1. Aceitação dos Termos</Text>
        <Text style={styles.paragraph}>
          Ao acessar e usar nosso aplicativo, você confirma que leu, entendeu e concorda em ficar vinculado a estes Termos de Uso e à nossa Política de Privacidade. Se você não concorda com estes termos, por favor, não utilize o aplicativo.
        </Text>

        <Text style={styles.sectionTitle}>2. Descrição do Serviço</Text>
        <Text style={styles.paragraph}>
          O Põe no Racha é uma plataforma que facilita o gerenciamento e a divisão de despesas entre grupos de pessoas (rachas). O aplicativo permite aos usuários criar rachas, adicionar participantes, registrar despesas e calcular o balanço de pagamentos para simplificar o acerto de contas.
        </Text>

        <Text style={styles.sectionTitle}>3. Contas de Usuário</Text>
        <Text style={styles.paragraph}>
          Para acessar a funcionalidade completa do aplicativo, você deve criar uma conta. Você concorda em:
        </Text>
        <Text style={styles.bulletPoint}>
          • Fornecer informações verdadeiras, precisas e completas durante o registro.
        </Text>
        <Text style={styles.bulletPoint}>
          • Manter a segurança de sua senha e se responsabilizar por todas as atividades que ocorram em sua conta.
        </Text>
        <Text style={styles.bulletPoint}>
          • Nos notificar imediatamente sobre qualquer uso não autorizado de sua conta.
        </Text>

        <Text style={styles.sectionTitle}>4. Plano Premium e Pagamentos</Text>
        <Text style={styles.paragraph}>
          O Põe no Racha oferece funcionalidades básicas gratuitas e recursos premium através de um plano de assinatura.
        </Text>
        <Text style={styles.bulletPoint}>
          • <Text style={styles.highlight}>Plano Premium Anual:</Text> Com uma <Text style={styles.highlight}>assinatura anual de R$ 9,90</Text>, o usuário terá acesso a número ilimitado de rachas, vínculo de bolso e experiência sem anúncios enquanto a assinatura estiver ativa.
        </Text>
        <Text style={styles.paragraph}>
          Os pagamentos são processados através do Stripe, um provedor de pagamentos seguro e certificado. A assinatura é renovada automaticamente anualmente, a menos que seja cancelada.
        </Text>

        <Text style={styles.sectionTitle}>5. Conteúdo Gerado pelo Usuário</Text>
        <Text style={styles.paragraph}>
          Você é o único responsável pelo conteúdo que cria no aplicativo, incluindo nomes de rachas, despesas e nomes de participantes. Você concorda em não criar conteúdo que seja ilegal, ofensivo, difamatório ou que viole os direitos de terceiros. Reservamo-nos o direito de remover qualquer conteúdo que viole estes termos.
        </Text>

        <Text style={styles.sectionTitle}>6. Conduta do Usuário</Text>
        <Text style={styles.paragraph}>
          Ao usar o Põe no Racha, você concorda em não:
        </Text>
        <Text style={styles.bulletPoint}>
          • Usar o serviço para qualquer finalidade ilegal ou fraudulenta.
        </Text>
        <Text style={styles.bulletPoint}>
          • Tentar obter acesso não autorizado aos nossos sistemas ou contas de outros usuários.
        </Text>
        <Text style={styles.bulletPoint}>
          • Interferir no funcionamento adequado do aplicativo.
        </Text>

        <Text style={styles.sectionTitle}>7. Limitação de Responsabilidade</Text>
        <Text style={styles.paragraph}>
          O Põe no Racha é fornecido como está. Não garantimos que os cálculos estarão sempre livres de erros ou que o serviço será ininterrupto. Em nenhuma circunstância seremos responsáveis por quaisquer danos diretos ou indiretos resultantes do uso ou da incapacidade de usar nosso serviço. A responsabilidade final pela verificação dos valores e pela realização dos pagamentos é dos próprios usuários.
        </Text>

        <Text style={styles.sectionTitle}>8. Alterações nos Termos</Text>
        <Text style={styles.paragraph}>
          Reservamo-nos o direito de modificar estes termos a qualquer momento. Notificaremos você sobre quaisquer alterações publicando os novos termos no aplicativo. A continuação do uso do aplicativo após tais alterações constitui sua aceitação dos novos termos.
        </Text>

        <Text style={styles.sectionTitle}>9. Contato</Text>
        <Text style={styles.paragraph}>
          Se você tiver alguma dúvida sobre estes Termos de Uso, entre em contato conosco pelo e-mail: <Text style={styles.email}>poenoracha@gmail.com</Text>
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}
